// Single source of truth for hostnames, API URLs, and Cognito identifiers
// used by both dashboard apps. Values here are public identifiers, not secrets.
//
// Imported by both browser code and Vite config (Node), so this module must
// stay free of unguarded browser APIs at top level.

export const HUB_URL = 'https://hub.verne.cc';

export const ADMIN_DOMAIN = 'vernerobotics.com';
export const SUPPORT_EMAIL = 'support@vernerobotics.com';

export const ADMIN_PROD_HOST = 'dashboard.verne.engineer';
export const FLEET_VIEWER_URL = 'https://app.vernerobotics.com/fleet';
export const INTERNAL_TOOLS_URL = 'https://verne.engineer';

/** The task explorer owns task creation. Reaching it needs a Cognito session, so
 * only the admin dashboard offers the link. */
export const TASK_EXPLORER_URL = `${INTERNAL_TOOLS_URL}/#/task-explorer`;

/** A task's page in the task explorer. */
export function taskExplorerUrl(taskName: string): string {
  return `${TASK_EXPLORER_URL}/task/${encodeURIComponent(taskName)}`;
}

/** The ops calendar — who runs which rotation, on which day, on which rig. It
 * owns every assignment write; a rig only reads them. Needs a Cognito session,
 * so only the admin dashboard offers the link. */
export function opsDashboardUrl(): string {
  return `${INTERNAL_TOOLS_URL}/#/data-collection`;
}

// Hosts here (and their subdomains) are treated as cloud — admin/user dashboards
// served on these require Cognito. Anything else is kiosk (robot-local) mode.
export const CLOUD_DOMAINS = ['verne.cc', 'vernerobotics.com', 'verne.engineer'] as const;

export const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1', '::1', '[::1]']);

export const COGNITO = {
  region: 'us-east-1',
  userPoolId: 'us-east-1_LdOWhx4iv',
  hostedUiDomain: 'https://auth.vernerobotics.com',
  adminClientId: '1sl9php1m3uijahoabgj67da2b',
  userClientId: '1jcs88b82g2bai35v90afs5k4f',
} as const;

export function cognitoAuthority(): string {
  return `https://cognito-idp.${COGNITO.region}.amazonaws.com/${COGNITO.userPoolId}`;
}

export function oidcStorageKey(clientId: string): string {
  return `oidc.user:${cognitoAuthority()}:${clientId}`;
}

export const isKiosk: boolean = (() => {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  if (CLOUD_DOMAINS.some((d) => host === d || host.endsWith('.' + d))) return false;
  if (LOOPBACK_HOSTS.has(host) && (import.meta as { env?: { DEV?: boolean } }).env?.DEV) return false;
  return true;
})();

/** True only when the dashboard is served from a public cloud host. */
export const isCloud: boolean = (() => {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return CLOUD_DOMAINS.some((d) => host === d || host.endsWith('.' + d));
})();
